import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, Box, Typography, Avatar, CircularProgress, IconButton, Grid, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CakeIcon from '@mui/icons-material/Cake';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import api from '../../api';

const baseUrl = api.defaults.baseURL || `http://${window.location.hostname}:8000/api/v1`;

// --- Helpers ---
const formatDate = (dateString) => {
  if (!dateString || dateString === '--') return '--';
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-GB', options);
};

// --- Reusable UI Components for the Card ---

const HorizontalInfoCard = ({ icon, iconColor, iconBg, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
    <Avatar sx={{ bgcolor: iconBg, color: iconColor, width: 38, height: 38 }}>
      {icon}
    </Avatar>
    <Box sx={{ overflow: 'hidden', width: '100%' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', mb: 0.2, fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem', wordBreak: 'break-word', lineHeight: 1.2 }}>
        {value || '--'}
      </Typography>
    </Box>
  </Box>
);

const VerticalInfoCard = ({ icon, iconColor, iconBg, label, value }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 2, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e2e8f0', textAlign: 'center', height: '100%' }}>
    <Avatar sx={{ bgcolor: iconBg, color: iconColor, width: 42, height: 42 }}>
      {icon}
    </Avatar>
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem', lineHeight: 1.2 }}>
        {value || '--'}
      </Typography>
    </Box>
  </Box>
);

// --- Main Popup Component ---

const EmployeeCardPopup = ({ open, onClose, empId }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && empId) {
      setLoading(true);
      api.get(`/employee-card/${empId}`)
        .then(res => {
          setData(res.data);
          setLoading(false);
        })
        .catch(err => {
          console.error("Failed to fetch employee card details", err);
          setLoading(false);
        });
    } else {
      setData(null);
    }
  }, [open, empId]);

  return (
    <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    PaperProps={{
        sx:{
            width: 480,          // 460-480 looks perfect
            maxWidth: '95vw',
            borderRadius: 3,
            overflow: 'hidden'
        }
    }}
>      {loading || !data ? (
        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 250 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {/* Header Area - Dark Blue Gradient */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #0c4da8 0%, #053982 100%)', 
            p: 3, pt: 4, pb: 3, 
            position: 'relative' 
          }}>
            <IconButton 
              onClick={onClose} 
              sx={{ position: 'absolute', top: 12, right: 12, bgcolor: 'rgba(255,255,255,0.1)', color: '#ffffff', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {/* Profile Picture with overlapping Active badge */}
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <Avatar 
                  src={`${baseUrl}/employees/${data.emp_id}/profile-picture`}
                  sx={{ width: 120, height: 120, border: '3px solid rgba(255,255,255,0.3)', bgcolor: 'primary.light', fontSize: '2.5rem' }}
                >
                  {data.first_name.charAt(0).toUpperCase()}
                </Avatar>
<Chip 
  icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2e7d32', ml: '8px !important' }} />}
  label="Active" 
  sx={{ 
    position: 'absolute', 
    bottom: -10, 
    left: '50%', 
    transform: 'translateX(-50%)',
    bgcolor: '#e8f5e9', 
    color: '#2e7d32', 
    fontWeight: 800, 
    height: 22, 
    fontSize: '0.7rem', 
    border: '2px solid #053982',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
  }} 
/>              </Box>
              
              <Box>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1, letterSpacing: 0.5 }}>
                  {data.first_name} {data.last_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={data.emp_id} 
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, borderRadius: 1.5 }} 
                  />
                  <Chip 
                    icon={<WorkspacePremiumIcon sx={{ color: 'white !important', fontSize: 16 }}/>} 
                    label={data.designation} 
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700, borderRadius: 1.5 }} 
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <DialogContent sx={{ p: 3, pt: 2.5 }}>
            
            {/* Section 1: Contact & Organization */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <ContactPageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 0.5, fontSize: '0.8rem' }}>
                CONTACT & ORGANIZATION
              </Typography>
            </Box>
            
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <HorizontalInfoCard icon={<EmailIcon fontSize="small"/>} iconColor="#1976d2" iconBg="#e3f2fd" label="Email" value={data.email} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <HorizontalInfoCard icon={<WorkIcon fontSize="small"/>} iconColor="#2e7d32" iconBg="#e8f5e9" label="Department" value={data.department} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <HorizontalInfoCard icon={<PersonIcon fontSize="small"/>} iconColor="#9c27b0" iconBg="#f3e5f5" label="Reporting to" value={data.manager_name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <HorizontalInfoCard icon={<LocationOnIcon fontSize="small"/>} iconColor="#ed6c02" iconBg="#fff3e0" label="Hometown" value={data.location} />
              </Grid>
            </Grid>
            
            {/* Section 2: Employment Details */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <AssignmentIndIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 0.5, fontSize: '0.8rem' }}>
                EMPLOYMENT DETAILS
              </Typography>
            </Box>
            
            <Grid container spacing={1.5}>
              <Grid item xs={4}>
                <VerticalInfoCard icon={<CalendarMonthIcon fontSize="small"/>} iconColor="#0288d1" iconBg="#e1f5fe" label="Date of Joining" value={formatDate(data.date_of_joining)} />
              </Grid>
              <Grid item xs={4}>
                <VerticalInfoCard icon={<TrendingUpIcon fontSize="small"/>} iconColor="#2e7d32" iconBg="#e8f5e9" label="Experience" value={data.experience} />
              </Grid>
              <Grid item xs={4}>
                <VerticalInfoCard icon={<CakeIcon fontSize="small"/>} iconColor="#9c27b0" iconBg="#f3e5f5" label="Birthday" value={data.birthday} />
              </Grid>
            </Grid>

          </DialogContent>
        </Box>
      )}
    </Dialog>
  );
};

export default EmployeeCardPopup;